import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AiMessageAssistMeta, AiMessageAssistRequest, AiReadAction, MessageDetail } from '@/api';
import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { describeProblem, useAiAvailability, type AiProblem } from '@/features/ai-assist/aiAssist';

/**
 * Summarize / Explain / Ask about the open message (ADR 0074).
 *
 *   - It exists only when the assistant is on with a key: `available` is
 *     false otherwise, and the reading pane draws no button, no sheet row and
 *     binds no key. Not a disabled control — no control.
 *   - The request names the message; the server reads it. Nothing of the
 *     message's text leaves this browser for the assistant.
 *   - One question at a time: "Ask a question…" is a single turn, and nothing
 *     from an earlier answer is sent with the next one. Nothing is kept once
 *     the panel closes or another message opens.
 */

export type AiSummaryPhase = 'closed' | 'asking' | 'running' | 'done' | 'error';

export interface AiSummaryState {
  /** The message the panel belongs to; another message means closed. */
  messageId: string | undefined;
  phase: AiSummaryPhase;
  action: AiReadAction;
  wholeThread: boolean;
  /** The answer is about the whole conversation (ADR 0077), not one message. */
  conversation: boolean;
  /** The question of an `ask`, as it was sent. */
  question: string;
  text: string;
  meta: AiMessageAssistMeta | undefined;
  partial: boolean;
  /** The model that answered ("Claude Sonnet 5.5"), once the answer is complete. */
  via: string | undefined;
  /** Why another model than the chosen one answered (the chosen one was retired). */
  modelNotice: string | undefined;
  problem: AiProblem | undefined;
}

const CLOSED: AiSummaryState = {
  messageId: undefined,
  phase: 'closed',
  action: 'summarize',
  wholeThread: false,
  conversation: false,
  question: '',
  text: '',
  meta: undefined,
  partial: false,
  via: undefined,
  modelNotice: undefined,
  problem: undefined,
};

export interface AiSummaryController {
  available: boolean;
  state: AiSummaryState;
  /** The panel is open on the message being shown. */
  open: boolean;
  summarize: (wholeThread?: boolean) => void;
  explain: () => void;
  /** Shows the question field. */
  startAsk: () => void;
  ask: (question: string) => void;
  stop: () => void;
  retry: () => void;
  close: () => void;
  /** Shift+S: open with a summary, or close. */
  toggle: () => void;
}

export interface AiSummaryOptions {
  /**
   * Ask about the whole conversation the message belongs to (ADR 0077): every
   * request carries `conversation: true` and never `wholeThread` (the server
   * refuses both). The conversation pane passes its newest message.
   */
  conversation?: boolean;
}

export function useAiSummary(
  message: Pick<MessageDetail, 'id' | 'accountId'> | undefined,
  options: AiSummaryOptions = {},
): AiSummaryController {
  const conversation = options.conversation === true;
  const api = useApi();
  const queryClient = useQueryClient();
  const { available } = useAiAvailability();
  const [raw, setState] = useState<AiSummaryState>(CLOSED);
  const abort = useRef<AbortController | null>(null);
  const last = useRef<AiMessageAssistRequest | null>(null);

  const messageId = message?.id;
  const accountId = message?.accountId;
  // Keyed by message id: opening another message shows no stale panel, with
  // no effect needed to clear it.
  const state = raw.messageId !== undefined && raw.messageId === messageId && available ? raw : CLOSED;

  // Leaving the message (or the pane) stops paying for an answer nobody reads.
  useEffect(() => () => abort.current?.abort(), [messageId]);

  const send = useCallback(
    (input: AiMessageAssistRequest) => {
      if (messageId === undefined || accountId === undefined) return;
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      // `conversation` and `wholeThread` are exclusive on the server: the
      // conversation already holds the thread.
      const { wholeThread: _wholeThread, ...rest } = input;
      const request: AiMessageAssistRequest = conversation ? { ...rest, conversation: true } : input;
      last.current = request;
      setState((s) => ({
        ...CLOSED,
        messageId,
        phase: 'running',
        action: request.action,
        wholeThread: request.wholeThread === true,
        conversation: request.conversation === true,
        question: request.question ?? '',
        // Keep what we learned about the message across its own re-asks.
        meta: s.messageId === messageId ? s.meta : undefined,
      }));
      api.ai
        .assistMessage(
          accountId,
          messageId,
          request,
          (piece) => {
            if (controller.signal.aborted) return;
            setState((s) => (s.phase === 'running' && s.messageId === messageId ? { ...s, text: s.text + piece } : s));
          },
          (meta) => {
            if (controller.signal.aborted) return;
            setState((s) => (s.messageId === messageId ? { ...s, meta } : s));
          },
          controller.signal,
        )
        .then((result) => {
          if (controller.signal.aborted) return;
          setState((s) => ({
            ...s,
            phase: s.text.trim() === '' ? 'error' : 'done',
            partial: result.stopReason === 'max_tokens',
            via: result.modelName ?? result.model,
            modelNotice: result.modelNotice,
            problem:
              s.text.trim() === ''
                ? { message: 'The assistant returned nothing. Try again.', settingsLink: false, retry: true }
                : undefined,
          }));
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          if (error instanceof ApiError && error.is(ApiErrorCodes.AINotEnabled)) {
            void queryClient.invalidateQueries({ queryKey: queryKeys.aiSettings() });
          }
          setState((s) => ({ ...s, phase: 'error', problem: describeProblem(error) }));
        });
    },
    [accountId, api, conversation, messageId, queryClient],
  );

  const summarize = useCallback(
    (wholeThread = false) => send({ action: 'summarize', ...(wholeThread ? { wholeThread: true } : {}) }),
    [send],
  );
  const explain = useCallback(
    () => send({ action: 'explain', ...(state.wholeThread ? { wholeThread: true } : {}) }),
    [send, state.wholeThread],
  );
  const ask = useCallback(
    (question: string) => {
      const q = question.trim();
      if (q === '') return;
      send({ action: 'ask', question: q, ...(state.wholeThread ? { wholeThread: true } : {}) });
    },
    [send, state.wholeThread],
  );
  const startAsk = useCallback(() => {
    if (messageId === undefined) return;
    abort.current?.abort();
    setState((s) => ({
      ...CLOSED,
      messageId,
      phase: 'asking',
      wholeThread: s.messageId === messageId && s.wholeThread,
      meta: s.messageId === messageId ? s.meta : undefined,
    }));
  }, [messageId]);
  const stop = useCallback(() => {
    abort.current?.abort();
    setState((s) =>
      s.phase === 'running' ? (s.text.trim() === '' ? CLOSED : { ...s, phase: 'done', partial: true }) : s,
    );
  }, []);
  const retry = useCallback(() => {
    if (last.current !== null) send(last.current);
    else summarize();
  }, [send, summarize]);
  const close = useCallback(() => {
    abort.current?.abort();
    setState(CLOSED);
  }, []);
  const open = state.phase !== 'closed';
  const toggle = useCallback(() => (open ? close() : summarize()), [close, open, summarize]);

  // Shift+S, bound only while there is something it can do.
  useKeyboardShortcuts({ 'shift+s': () => toggle() }, { enabled: available && messageId !== undefined });

  return { available, state, open, summarize, explain, startAsk, ask, stop, retry, close, toggle };
}
